// gccValueLeakIndicators.js
// Comprehensive Value Leakage Indicators Database
// Expert-curated mapping of all possible indicators to GCC Framework sub-types
// Exact string matching with gccFramework.js for production use

export const GCC_VALUE_LEAK_INDICATORS = {
  // Financial Value Leakage Indicators
  financial: {
    duplicateSystems: [
      "Multiple invoices for software serving identical functions",
      "Budget allocations to different departments for same-capability tools",
      "Low individual usage rates (<30%) on multiple systems with overlapping features",
      "Employees maintaining logins for 2+ systems with similar functionality",
      "Data reconciliation efforts between systems performing same function",
      "Internal debates over which system is the source of truth",
      "Presence of 3+ vendors in a single software category (CRM, ERP, etc.)",
      "Lack of enterprise-wide software asset management process",
      "Separate support contracts for redundant systems",
      "Dedicated IT resources maintaining multiple similar platforms"
    ],
    underutilizedLicenses: [
      "License utilization reports showing consistent sub-60% usage of purchased seats",
      "Premium feature adoption rates below 20% across user base",
      "Paying for enterprise tiers while primarily using standard tier features",
      "Automatic annual license renewals without usage review",
      "Lack of formal license true-up/down process",
      "Frequent requests for temporary licenses or guest access",
      "Unused seat licenses discovered during audits",
      "Software vendors charging for shelfware features",
      "No centralized license allocation and reclamation process"
    ],
    redundantProcesses: [
      "Employees manually transferring data between systems (email to ERP, PDF to spreadsheet)",
      "High rates of data correction and rework in core processes",
      "Internal audit findings citing data integrity issues from manual entry",
      "Employee surveys citing administrative burden and paperwork time",
      "Proliferation of shadow IT spreadsheets and databases for core processes",
      "Skilled FTE spending >15% time on repetitive data tasks",
      "Multiple approval layers for simple data updates",
      "Process mining reveals high manual touchpoints in workflow"
    ],
    inefficientProcurement: [
      "Same goods/services purchased from multiple vendors at different prices across business units",
      "Inability to negotiate volume discounts due to fragmented spending",
      "Multiple disparate vendor management and payment processes",
      "Inconsistent security and compliance vetting of suppliers",
      "Lack of centralized supplier performance tracking",
      "Duplicate vendor records in procurement system",
      "Different contract terms for same category of spend",
      "No spend analytics across organization"
    ],
    pricingInefficiencies: [
      "Competitors offering similar value at lower prices or superior value at same price",
      "High rates of price-based churn or prolonged sales cycles with extensive negotiations",
      "Static cost-plus pricing models without value-based components",
      "Low price realization (actual sale price vs list price)",
      "High discounting rates across sales teams",
      "No dynamic pricing based on market conditions",
      "Pricing decisions made in silos without centralized strategy",
      "Customer complaints about pricing inconsistency"
    ]
  },

  // Strategic Intelligence Leakage Indicators
  strategic: {
    competitiveBlindspots: [
      "Consistently surprised by competitor product launches and strategic pivots",
      "No dedicated personnel or budget for competitive intelligence",
      "Strategic plans lack competitive counter-move analysis",
      "Competitive intelligence siloed in sales/marketing, not integrated into R&D",
      "No systematic tracking of competitor financials, hiring, or patent activity",
      "Market share loss without understanding competitor tactics",
      "Inability to articulate competitor's core strategy and differentiation"
    ],
    slowTimeToMarket: [
      "Idea-to-revenue cycle times 2-3x longer than agile competitors",
      "More than 5 approval gates for new product initiatives",
      "Innovation governed by same stage-gates as core business operations",
      "Product portfolio dominated by mature/declining products",
      "Long development cycles for minor feature updates",
      "Missed market windows for seasonal or trend-based opportunities",
      "Bureaucratic approval processes slowing experimentation"
    ],
    innovationPipelineGaps: [
      "Inability to see how core competencies apply to adjacent markets",
      "Dismissing disruptive business models as not our industry",
      "Innovation pipeline focused only on incremental feature improvements",
      "No process for scanning, partnering with, or acquiring emerging startups",
      "Lack of disruptive innovation in product roadmap",
      "Weak connection between R&D and market needs",
      "No formal idea capture and evaluation system"
    ]
  },

  // Digital Transformation Leakage Indicators
  digital: {
    legacySystemDrag: [
      "IT budget with >40% allocated to keeping lights on versus new development",
      "Inability to integrate with modern APIs",
      "Simple changes require months of development and testing",
      "Systems out of vendor support requiring custom security patches",
      "Difficulty hiring developers for legacy technologies",
      "Key personnel retirement creating knowledge gaps",
      "High maintenance costs for outdated platforms",
      "Security vulnerabilities in unsupported systems"
    ],
    digitalAdoptionGaps: [
      "Low active usage (<40%) of deployed collaboration platforms",
      "Small fraction (<20%) of available software features actively used",
      "Constant user support tickets for basic functionality",
      "Lack of ongoing digital literacy training programs",
      "Resistance to using new digital tools",
      "Poor user experience leading to workarounds",
      "Multiple tools for same function causing confusion"
    ],
    dataMonetizationMissed: [
      "Critical customer data locked in departmental systems",
      "Strategic decisions made based on gut feel rather than data analytics",
      "No exploration of data-as-a-service or information products",
      "Untapped operational data that could create new revenue streams",
      "Limited analytics capability across organization",
      "Data treated as cost center rather than asset",
      "No data product roadmap or monetization strategy"
    ]
  },

  // Operational Value Leakage Indicators
  operational: {
    supplyChainInefficiencies: [
      "Consistently high inventory days or frequent stockouts",
      "Demand forecasts consistently inaccurate (>20% error)",
      "High variability in supplier lead times and quality",
      "Bullwhip effect visible in inventory fluctuations",
      "Poor visibility into supplier capacity and constraints",
      "Manual purchase order and tracking processes",
      "Lack of integrated supply chain planning system"
    ],
    manufacturingInefficiencies: [
      "OEE consistently below 85% (world-class >90%)",
      "Unplanned equipment downtime regular occurrence",
      "Low first-pass yield rates requiring significant rework",
      "Extended changeover times between production runs",
      "Capacity constraints not addressed systematically",
      "Quality variations across shifts or production lines",
      "Inefficient material flow and layout"
    ],
    serviceDeliveryGaps: [
      "Billable utilization rates below 70% in service organizations",
      "Consistent project overruns on time and budget",
      "High-value talent assigned to low-value tasks",
      "Poor scheduling and resource allocation",
      "Skill mismatches on project teams",
      "Lack of capacity planning for peak demand periods",
      "Inconsistent service quality across teams"
    ],
    manualWorkflows: [
      "Processes with numerous manual handoffs and approvals",
      "Data re-entry between different systems and departments",
      "Inconsistent process execution leading to compliance issues",
      "High error rates in manual process steps",
      "Lack of workflow automation for repetitive tasks",
      "Process variations between teams doing same work",
      "Manual reporting and data aggregation"
    ]
  },

  // Human Capital Value Leakage Indicators
  humanCapital: {
    employeeTurnover: [
      "High turnover (>15%) among high-performers and mission-critical roles",
      "Exit interviews citing lack of growth, poor management, or stagnant culture",
      "High and rising costs of recruitment and onboarding",
      "Critical operational knowledge concentrated in few individuals",
      "No documentation or succession plan for key roles",
      "Departure of experts causing operational disruptions",
      "Difficulty backfilling specialized positions"
    ],
    skillGaps: [
      "Formal assessment reveals gaps between current skills and future needs",
      "Key initiatives delayed due to inability to find required talent",
      "Using expensive external consultants for core strategic capabilities",
      "Training programs not aligned with strategic skill requirements",
      "Employees lacking digital literacy for modern tools",
      "Skill obsolescence in rapidly changing technical domains",
      "No skills inventory or capability mapping"
    ],
    talentUnderutilization: [
      "Low rates of internal hiring for open positions",
      "Employee-led innovations or improvements are rare",
      "Engagement surveys show low scores on career development",
      "High-potential employees stuck in routine roles",
      "Lack of internal mobility and cross-training",
      "Employee skills not fully leveraged in current assignments",
      "No system for capturing and acting on employee ideas"
    ]
  },

  // Customer Experience Leakage Indicators
  customer: {
    customerChurn: [
      "High customer churn rate, especially in first 90 days",
      "Declining or low Net Promoter Score and Customer Satisfaction scores",
      "High volume of support contacts and complaints",
      "Decreasing Customer Lifetime Value metrics",
      "Customers downgrading service tiers or reducing spend",
      "Negative social media sentiment and reviews",
      "Poor renewal rates for subscription services"
    ],
    serviceGaps: [
      "Different customers or regions receive significantly different service levels",
      "High error rates in order fulfillment and invoicing",
      "Inconsistent service quality across support channels",
      "Long resolution times for customer issues",
      "Lack of service standards and quality monitoring",
      "Customer complaints about process variations",
      "No unified view of customer interactions"
    ],
    onboardingFriction: [
      "Long delay between customer sign-up and first value realization",
      "High percentage of new users never complete setup process",
      "Complex configuration and implementation requirements",
      "Poor guidance and documentation for new users",
      "Slow activation processes requiring manual intervention",
      "High early-stage support ticket volume",
      "Customers struggling to achieve key outcomes"
    ],
    upsellMissed: [
      "Low ratio of products per customer (cross-sell ratio)",
      "Existing customers show flat year-over-year spending growth",
      "Interactions primarily transactional, no strategic account planning",
      "No system for identifying expansion opportunities",
      "Poor understanding of customer's broader needs",
      "Sales teams focused only on new customer acquisition",
      "Missed contract renewal opportunities"
    ]
  },

  // Compliance & Regulatory Leakage Indicators
  compliance: {
    regulatoryPenalties: [
      "Internal/external audits regularly uncover repeat compliance failures",
      "Legal team constantly fire-fighting new regulations reactively",
      "Employee compliance training infrequent or has low completion rates",
      "Internal policies outdated and not aligned with current regulations",
      "Compliance viewed as Legal's problem, not integrated into business",
      "Regulatory change monitoring process absent or ineffective",
      "Control deficiencies cited in multiple audit cycles"
    ],
    securityVulnerabilities: [
      "Growing backlog of unpatched critical/high-severity security vulnerabilities",
      "High failure rate in phishing simulation tests",
      "Widespread over-provisioning of user access rights",
      "No tested formal incident response plan",
      "Slow detection and containment times for security events",
      "Security bolted on as afterthought in new projects",
      "Poor access review and certification processes"
    ],
    businessContinuityGaps: [
      "BCP/DRP plans exist on paper but not tested annually",
      "Critical operations depend on single vendor or data center",
      "Documented Recovery Time Objective unachievable with current technology",
      "Crisis management team unaware of roles or never participated in drills",
      "No viable failover for critical business functions",
      "Single points of failure in technology infrastructure",
      "Inadequate backup and recovery capabilities"
    ]
  },

  // Innovation Pipeline Leakage Indicators
  innovation: {
    innovationFriction: [
      "Funnel where over 70% ideas killed by internal gatekeeping",
      "Culture where failed experiments are punished",
      "Promising ideas approved but given no dedicated budget",
      "Innovation forced through rigid stage-gate process",
      "Risk-averse culture blocking experimental approaches",
      "Long approval cycles for prototyping and testing",
      "Lack of dedicated innovation resources and sandbox environments"
    ],
    adjacencyBlindness: [
      "Business unit strategies focused only on existing markets",
      "No periodic mapping of core competencies to adjacent opportunities",
      "Only listening to expressed needs of current customers",
      "Dismissing market adjacencies as not core business",
      "No process for exploring white space opportunities",
      "Inability to see disruptive threats from adjacent industries",
      "Limited portfolio beyond current market boundaries"
    ],
    ecosystemDisconnect: [
      "Cultural bias against partnering with or acquiring startups",
      "No formal process for technology scouting or startup engagement",
      "Multiple successful POCs that never scale to production",
      "Not-invented-here syndrome blocking external innovation",
      "Poor venture capital and startup ecosystem relationships",
      "Technology adoption lagging behind startup competitors",
      "Isolation from academic and research institution partnerships"
    ]
  },

  // Additional categories continue with same structure...
  talent: {
    leadershipGaps: [
      "No ready internal candidates for >30% of critical leadership roles",
      "High-potential employees not given stretch assignments or mentoring",
      "Pattern of hiring senior leaders from outside due to lack of internal bench",
      "Succession plans outdated or not actionable",
      "Leadership development programs not aligned with future needs",
      "Key leadership roles vacant for extended periods",
      "No systematic leadership competency assessment"
    ],
    collaborationInefficiencies: [
      "Employee time tracking shows >25% time spent in meetings",
      "Meetings routinely lack clear agendas and action items",
      "Multiple disconnected communication tools causing information loss",
      "Excessive meeting proliferation without productivity gains",
      "Poor information sharing across departments",
      "Decision delays due to communication bottlenecks",
      "Lack of collaboration tools standardization"
    ],
    strategicMisalignment: [
      "Departmental KPIs in direct conflict with each other",
      "Projects fail due to departmental silos and lack of collaboration",
      "Employees confused by too many top priority initiatives",
      "Strategy not effectively cascaded to operational levels",
      "Conflicting goals between headquarters and business units",
      "No clear line of sight from individual goals to company strategy",
      "Initiative overload without prioritization framework"
    ]
  },

  technology: {
    legacySystemDrag: [
      "Complex web of point-to-point custom integrations between systems",
      "Reliance on retired experts to maintain critical legacy platforms",
      "High integration costs for simple system enhancements",
      "Inability to leverage modern cloud and SaaS capabilities",
      "Custom code maintenance consuming majority of development resources",
      "Vendor lock-in preventing technology modernization",
      "Security risks from unsupported legacy applications"
    ],
    dataSilos: [
      "Business units resist centralizing data, treating it as their asset",
      "All data requests must go through central IT creating long delays",
      "Different departments produce conflicting business metrics",
      "No single view of customer across organization",
      "Data integration projects consistently over budget and behind schedule",
      "Departmental ownership blocking data accessibility",
      "Inconsistent data definitions across systems"
    ],
    systemIntegrationGaps: [
      "Critical business systems lack modern APIs forcing manual work",
      "Dedicated teams spend time manually reconciling data between systems",
      "Point solutions creating process disconnects",
      "Lack of API strategy and integration architecture",
      "Manual data transfers via CSV files and email",
      "Process delays due to system integration failures",
      "No enterprise service bus or integration platform"
    ]
  },

  process: {
    manualWorkflows: [
      "Process mining reveals high number of variants for single process",
      "Long and highly variable cycle times for core processes",
      "Significant portion of process output requires correction",
      "More than 20% process instances are exceptions requiring manual handling",
      "Lack of process standardization across organization",
      "Manual approval workflows slowing decision making",
      "Paper-based processes in digital age"
    ],
    salesCycleDelays: [
      "Sales funnel with dramatic drop-off between proposal and contract",
      "Deals stagnate waiting for internal legal/finance/security approvals",
      "Sales teams lack compliant personalized content for prospect objections",
      "Complex approval processes for standard deals",
      "Poor lead routing and qualification processes",
      "Administrative burden on sales teams",
      "Lack of sales process automation"
    ]
  },

  data: {
    dataQualityIssues: [
      "Business leaders question report accuracy and use own spreadsheets",
      "Customer/product master data contains 5-10% duplicate entries",
      "Critical data domains have no officially assigned business owner",
      "Dedicated team required to manually clean data for reporting",
      "Inconsistent data formats and standards across systems",
      "Poor data validation at point of entry",
      "No master data management strategy"
    ],
    unmonetizedDataAssets: [
      "Valuable datasets used only for internal operational reporting",
      "No roadmap for creating new revenue streams from data assets",
      "Competitors launching services based on analytics you possess",
      "Data treated as byproduct rather than primary asset",
      "No data product management function",
      "Limited external data partnerships or exchanges",
      "Inability to package and sell data insights"
    ]
  },

  supplyChain: {
    inventoryMismanagement: [
      "High inventory carrying costs or frequent stockouts disrupting operations",
      "Poor demand forecasting accuracy leading to bullwhip effect",
      "Excess obsolete inventory requiring write-offs",
      "No integrated inventory optimization system",
      "Poor supplier collaboration on inventory management",
      "Inadequate safety stock calculations",
      "Manual inventory counting and reconciliation"
    ],
    logisticsInefficiencies: [
      "Inefficient transportation routes and load planning",
      "High transportation costs as percentage of revenue",
      "Frequent delivery delays and missed appointments",
      "Poor carrier performance and relationship management",
      "Lack of real-time shipment tracking",
      "Manual freight audit and payment processes",
      "No logistics optimization and route planning systems"
    ]
  },

  quality: {
    reworkCosts: [
      "High and rising Cost of Poor Quality as percentage of total cost",
      "Quality ensured through large final inspection team",
      "Incoming materials from suppliers have high defect rates",
      "Significant scrap and waste in manufacturing processes",
      "High internal failure costs before product reaches customer",
      "Lack of mistake-proofing in process design",
      "Reactive quality control rather than preventive"
    ],
    defectEscapes: [
      "Increasing trend in warranty claims and associated costs",
      "High volume of customer support calls for product failures",
      "Negative press/social media about product quality issues",
      "Field failure rates above industry benchmarks",
      "Customer complaints about reliability and durability",
      "Quality issues discovered after product shipment",
      "Poor root cause analysis for defects"
    ]
  },

  sustainability: {
    carbonFootprint: [
      "Facing current or anticipated carbon taxes and regulatory fines",
      "Energy consumption per unit output not tracked or increasing",
      "Assets at risk of stranding due to climate regulations",
      "High greenhouse gas emissions across operations",
      "No carbon reduction strategy or targets",
      "Inefficient energy usage in facilities and manufacturing",
      "Poor environmental compliance record"
    ],
    resourceInefficiency: [
      "Pure take-make-dispose model with no circular economy initiatives",
      "High and rising costs for waste disposal and landfill fees",
      "By-products treated as waste rather than potential revenue streams",
      "Low recycling rates across operations",
      "Linear processes with no closed-loop systems",
      "Material waste in production processes",
      "No resource optimization programs"
    ]
  },

  governance: {
    decisionDelays: [
      "Decisions repeatedly deferred to gather more data or achieve consensus",
      "Complex web of committees creating approval bottlenecks",
      "Documented instances of missed market windows due to slow decisions",
      "Analysis paralysis on strategic initiatives",
      "Lack of clear decision rights and accountability",
      "Consensus culture preventing timely action",
      "No escalation path for stuck decisions"
    ],
    oversightGaps: [
      "Risk management fragmented across organization with no consolidated view",
      "Internal/external auditors cite same control weaknesses year after year",
      "Organization repeatedly caught off-guard by emerging risks",
      "No enterprise risk management framework",
      "Poor risk culture and awareness",
      "Control failures leading to operational losses",
      "Inadequate board oversight of key risks"
    ]
  },

  cultural: {
    innovationResistance: [
      "Phrases like that's not how we do things here are common",
      "Employee cynicism and resistance to new corporate initiatives",
      "Blame culture where failures lead to punishment rather than learning",
      "Change management treated as optional rather than essential",
      "Not-invented-here syndrome blocking external ideas",
      "Comfort with status quo preventing necessary change",
      "Leadership not modeling desired cultural behaviors"
    ],
    collaborationBarriers: [
      "Departmental goals reward siloed behavior at company expense",
      "Knowledge and data used as power source within departments",
      "Multiple departments working on similar projects unaware of each other",
      "Poor cross-functional teamwork on key initiatives",
      "Information hoarding between teams",
      "Turf wars and territory protection",
      "Lack of shared goals and incentives"
    ]
  },

  brand: {
    customerTrustErosion: [
      "Steady decline in positive sentiment on review sites and social media",
      "Market research shows gap between company and customer brand perception",
      "History of poorly handled product issues or PR incidents",
      "Privacy breaches or data mishandling incidents",
      "Poor transparency in business practices",
      "Broken promises to customers or stakeholders",
      "Negative media coverage affecting reputation"
    ],
    employerBrandGaps: [
      "Low scores on employer review sites like Glassdoor",
      "High rejection rate from desirable job candidates citing culture",
      "Need to pay significant salary premium to attract talent",
      "Poor social responsibility and community engagement",
      "Weak employee value proposition",
      "Talent acquisition challenges for key roles",
      "High recruitment costs due to poor brand perception"
    ]
  },

  ecosystem: {
    partnershipInefficiencies: [
      "Partnerships lack clear measurable joint business objectives",
      "High partnership activity but low tangible value creation",
      "No clear governance structure for key partnerships",
      "Misaligned incentives between partners",
      "Limited collaboration beyond contractual obligations",
      "Value leakage in alliance management",
      "Duplicate efforts across partnership portfolio"
    ],
    supplierInnovationMissed: [
      "Transactional relationships focused only on price negotiations",
      "Suppliers never invited to contribute to product/process roadmaps",
      "Limited innovation sharing with supply chain partners",
      "Missed supplier capabilities and expertise",
      "No collaborative innovation programs with key suppliers",
      "Price-focused procurement blocking value co-creation",
      "Poor supplier relationship management"
    ],
    academicResearchDisconnect: [
      "Slow technology transfer from research institutions",
      "Limited university engagement and partnerships",
      "Research silos preventing commercialization",
      "Long delays between research and product application",
      "No formal technology transfer programs",
      "Poor IP management from research collaborations",
      "Academic partnerships not aligned with business strategy"
    ],
    platformNetworkEffects: [
      "Closed architecture with no public APIs for partners",
      "No developer ecosystem or API strategy",
      "All innovation driven internally with no external contributions",
      "Missed ecosystem value creation opportunities",
      "No multi-sided platform strategy",
      "Limited APIs blocking integration possibilities",
      "Platform readiness gaps in technology architecture"
    ],
    marketplaceCreationMissed: [
      "Product-only mindset with no multi-sided business model",
      "No marketplace platform strategy",
      "Limited ecosystem around core products",
      "Missed network effects from platform approach",
      "Single-sided revenue model limitations",
      "No platform economics evaluation",
      "Inability to capture ecosystem value"
    ]
  },

  organizationalLearning: {
    tacitKnowledgeLoss: [
      "Operations dependent on few tribal elders for critical decisions",
      "Critical processes poorly documented or documentation outdated",
      "No identified backup for >20% of mission-critical roles",
      "Expert dependencies creating single points of failure",
      "Poor knowledge capture from departing experts",
      "Relearning cycles after key personnel departures",
      "No systematic knowledge retention programs"
    ],
    lessonsLearnedAmnesia: [
      "Projects end without formal lessons learned reviews",
      "Same types of risks derail multiple projects repeatedly",
      "No institutional memory of past successes and failures",
      "Limited learning transfer between projects",
      "No repository for capturing and sharing lessons",
      "Post-mortem findings not actioned",
      "History of repeated mistakes"
    ],
    crossPollinationBarriers: [
      "Departmental boundaries preventing knowledge sharing",
      "No communities of practice or expert networks",
      "Limited sharing of best practices across organization",
      "Duplicate innovation efforts in different departments",
      "Poor cross-functional collaboration on learning",
      "Knowledge silos between business units",
      "No platforms for sharing insights and innovations"
    ],
    adaptationVelocityGaps: [
      "Bureaucratic decision-making slowing strategic pivots",
      "Rigid annual planning cycles preventing agile response",
      "Slow market response compared to competitors",
      "Missed opportunities due to organizational inertia",
      "Poor market sensing and signal detection",
      "Inability to execute rapid strategy adjustments",
      "Change resistance blocking necessary adaptations"
    ],
    technologyAdoptionLag: [
      "Complex procurement processes slowing technology acquisition",
      "Legacy integration challenges blocking new technology implementation",
      "Skill gaps preventing adoption of emerging technologies",
      "Resistance to new technology from users",
      "2-4 year enterprise adoption lag behind startups",
      "Pilot projects that never scale to production",
      "No emerging technology adoption capability"
    ]
  },

  strategicOptionality: {
    technologyOptionality: [
      "High switching costs preventing vendor changes",
      "Heavy reliance on vendor-specific customizations",
      "Inability to negotiate favorable terms due to lack of alternatives",
      "Single vendor dependencies for critical systems",
      "Proprietary systems limiting integration options",
      "Architectural inflexibility blocking innovation",
      "Vendor lock-in risk assessment not performed"
    ],
    businessModelOptionality: [
      "Over 80% revenue from single product line or customer segment",
      "Only one business model in operation",
      "No business model experimentation or innovation",
      "Market disruption vulnerability from new models",
      "Limited revenue stream diversification",
      "Inability to pivot business model when needed",
      "No portfolio of business model experiments"
    ],
    talentOptionality: [
      "Narrow skill sets limiting workforce agility",
      "No cross-training or talent mobility programs",
      "Limited ability to pivot to new opportunities",
      "Skill obsolescence risk in key areas",
      "Poor continuous reskilling capabilities",
      "Rigid job descriptions blocking flexibility",
      "Inability to redeploy talent quickly"
    ],
    scenarioPlanningGaps: [
      "No war-gaming or scenario planning exercises",
      "Weak signal detection for emerging risks",
      "Strategic surprise from unexpected events",
      "Poor disruption response capabilities",
      "No strategic foresight function",
      "Inadequate preparation for potential futures",
      "Reactive rather than proactive strategy"
    ],
    portfolioImbalance: [
      "Over 90% resources allocated to core business",
      "Insufficient investment in future growth opportunities",
      "Resource misallocation blocking innovation",
      "Growth stagnation from lack of new ventures",
      "No balanced portfolio across horizons",
      "Too much core investment, not enough future bets",
      "Inability to fund strategic experiments"
    ]
  },

  quantum: {
    algorithmAwareness: [
      "Quantum computing never discussed in technology or strategy forums",
      "Inability to identify quantum advantage candidates in business problems",
      "Competitors publishing quantum research or forming partnerships",
      "No awareness of quantum computing implications for industry",
      "Technology blindspot regarding quantum advancements",
      "No quantum algorithm research or education",
      "Dismissal of quantum as irrelevant science project"
    ],
    talentPipeline: [
      "No relationships with universities having quantum research programs",
      "Complete lack of in-house quantum computing knowledge",
      "No strategy for building quantum talent pipeline",
      "Skill shortages in quantum-related disciplines",
      "Limited understanding of quantum business applications",
      "No educational partnerships for quantum skills",
      "Inability to recruit quantum expertise"
    ],
    useCaseIdentification: [
      "No research into quantum applications for business problems",
      "Application blindspots for quantum advantage",
      "Limited experimentation with quantum computing",
      "Follower position in industry quantum adoption",
      "No quantum use case evaluation process",
      "Inability to articulate quantum opportunity areas",
      "No pilot projects exploring quantum capabilities"
    ]
  },

  gccSpecific: {
    costCenterTrap: [
      "GCC budget categorized purely as cost center with run-the-business focus",
      "GCC leadership excluded from strategic business planning sessions",
      "Work portfolio dominated by transactional tasks and tickets",
      "No strategic projects assigned to GCC teams",
      "Budget constraints limiting GCC value creation",
      "Back-office perception blocking strategic influence",
      "Limited executive engagement with GCC leadership"
    ],
    innovationCeiling: [
      "GCC performance measured only on cost savings and efficiency",
      "Highly skilled GCC teams assigned only routine maintenance work",
      "No formal channel for GCC to propose strategic initiatives",
      "Capability boundaries too narrow for value contribution",
      "Missed transformation opportunities from GCC talent",
      "Limited innovation metrics for GCC performance",
      "Reactive operations rather than proactive innovation"
    ],
    talentCeiling: [
      "GCC marketed as low-cost center undermining talent attraction",
      "Lack of visible career progression to global leadership roles",
      "Reputation for offering only repetitive non-innovative work",
      "Limited value creation potential from current talent model",
      "Talent attraction challenges for premium roles",
      "Career path limitations blocking talent retention",
      "Skill gaps from limited challenging assignments"
    ],
    centerOfExcellenceMissed: [
      "Remaining service delivery focused rather than expertise leverage",
      "Limited knowledge sharing across GCC teams",
      "No thought leadership originating from GCC",
      "Expertise silos within GCC preventing value creation",
      "No communities of practice or knowledge platforms",
      "Poor utilization of specialized GCC expertise",
      "Service mindset blocking excellence development"
    ],
    globalNetworkIsolation: [
      "Isolated GCC operations without global collaboration",
      "Duplicated efforts across different GCC locations",
      "No scale advantages from global network",
      "Inefficient processes not standardized globally",
      "Poor knowledge sharing between global centers",
      "Limited best practice exchange",
      "No shared service platforms across network"
    ]
  }
};

// Export utility functions for indicator retrieval
export const getIndicatorsBySubType = (category, subType) => {
  return GCC_VALUE_LEAK_INDICATORS[category]?.[subType] || [];
};

export const getAllIndicatorsForCategory = (category) => {
  return GCC_VALUE_LEAK_INDICATORS[category] || {};
};

export const searchIndicators = (searchTerm) => {
  const results = {};
  Object.entries(GCC_VALUE_LEAK_INDICATORS).forEach(([category, subTypes]) => {
    Object.entries(subTypes).forEach(([subType, indicators]) => {
      const matchingIndicators = indicators.filter(indicator => 
        indicator.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (matchingIndicators.length > 0) {
        if (!results[category]) results[category] = {};
        results[category][subType] = matchingIndicators;
      }
    });
  });
  return results;
};